import { useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { colors, fonts, media, copperGradient, prefersReducedMotion } from "../../styles/theme";

gsap.registerPlugin(ScrollTrigger);

export const Container = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;

  @media ${media.small} {
    padding: 0 40px;
  }
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${fonts.mono};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${(p) => p.$color || colors.moss};

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 0 4px ${(p) => (p.$onDark ? "rgba(250,247,241,0.12)" : "rgba(79,100,69,0.14)")};
  }
`;

export const Display = styled.h2`
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(40px, 6vw, 84px);
  line-height: 1;
  letter-spacing: -0.03em;
  color: ${(p) => (p.$onDark ? colors.linen : colors.ink)};
  text-wrap: balance;

  em {
    font-style: normal;
    color: ${(p) => (p.$onDark ? colors.copperLight : colors.copper)};
  }
`;

export const Lead = styled.p`
  font-family: ${fonts.body};
  font-size: clamp(18px, 1.6vw, 21px);
  line-height: 1.6;
  color: ${(p) => (p.$onDark ? "rgba(250,247,241,0.78)" : colors.bark)};
  max-width: 560px;
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 52px;
  padding: 0 26px;
  border-radius: 999px;
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.01em;
  white-space: nowrap;
  cursor: pointer;
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.3s ease,
    background-color 0.3s ease, color 0.3s ease;

  svg {
    transition: transform 0.3s ease;
  }
  &:hover svg {
    transform: translateX(4px);
  }
`;

export const PrimaryButton = styled(Link)`
  ${buttonBase}
  color: ${colors.linen};
  background: ${copperGradient};
  background-size: 160% 100%;
  background-position: 100% 0;
  box-shadow: 0 10px 30px -12px rgba(143, 83, 41, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.25);

  &:hover {
    transform: translateY(-2px);
    background-position: 0 0;
    box-shadow: 0 18px 40px -14px rgba(143, 83, 41, 0.8), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
`;

export const GhostButton = styled.a`
  ${buttonBase}
  color: ${(p) => (p.$onDark ? colors.linen : colors.ink)};
  border: 1px solid ${(p) => (p.$onDark ? "rgba(250,247,241,0.35)" : colors.stone)};
  background: ${(p) => (p.$onDark ? "transparent" : "rgba(250,247,241,0.6)")};

  &:hover {
    transform: translateY(-2px);
    border-color: ${(p) => (p.$onDark ? colors.linen : colors.ink)};
  }
`;

export const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 8h10m0 0L9 4m4 4l-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Fades/slides up every [data-reveal] element inside `scopeRef` as it scrolls into view.
// Uses IntersectionObserver so it follows the live layout, even after fonts and videos
// change the page height.
export const useReveal = (scopeRef) => {
  useLayoutEffect(() => {
    const scope = scopeRef.current;
    if (!scope || prefersReducedMotion() || typeof IntersectionObserver === "undefined") return undefined;
    const ctx = gsap.context(() => {}, scope);
    const els = gsap.utils.toArray(scope.querySelectorAll("[data-reveal]"));
    ctx.add(() => gsap.set(els, { opacity: 0, y: 40 }));
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          ctx.add(() =>
            gsap.to(entry.target, {
              opacity: 1,
              y: 0,
              duration: 1.1,
              ease: "power3.out",
              delay: parseFloat(entry.target.dataset.reveal) || 0,
            })
          );
        });
      },
      { rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      ctx.revert();
    };
  }, [scopeRef]);
};

// Plays a muted looping video only while it is on screen.
export const useInViewPlayback = (videoRef) => {
  useLayoutEffect(() => {
    const video = videoRef.current;
    if (!video || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.15 }
    );
    io.observe(video);
    return () => io.disconnect();
  }, [videoRef]);
};
